import { z } from 'zod'

import { api } from '#admin/lib/api-client.ts'

/**
 * What the exhibitor page shows beside the booking: the passes of the stand, and its fit-out. The
 * registration page asks here for the stand that its badges were issued for.
 */

const base = '/v1/exhibitor/admin'
const withSignal = (signal?: AbortSignal) => (signal ? { signal } : {})

// --- passes -------------------------------------------------------------------------------------

export const passSchema = z.object({
  id: z.string(),
  holderName: z.string(),
  email: z.string(),
  jobTitle: z.string().nullable(),
  ticketTypeId: z.string().nullable().default(null),
  issuedTicketId: z.string().nullable(),
  registrationId: z.string().nullable().default(null),
})

export type Pass = z.infer<typeof passSchema>

export const passesSchema = z.object({
  entitled: z.number(),
  claimed: z.number(),
  items: z.array(passSchema),
})

export type Passes = z.infer<typeof passesSchema>

export function fetchPasses(applicationId: string, signal?: AbortSignal): Promise<Passes> {
  return api.get(`${base}/applications/${applicationId}/convention-passes`, {
    schema: passesSchema,
    ...withSignal(signal),
  })
}

/** The registrations that hold the stand's badges, each once, in the order of the passes. */
export function passRegistrations(passes: readonly Pass[]): string[] {
  return [
    ...new Set(
      passes.flatMap((pass) => (pass.registrationId === null ? [] : [pass.registrationId])),
    ),
  ]
}

export const standSchema = z.object({
  applicationId: z.string(),
  code: z.string().nullable(),
  companyName: z.string(),
})

export type Stand = z.infer<typeof standSchema>

/** The stand a registration of passes was issued for. The API answers 404 for any other. */
export function fetchStandOfRegistration(registrationId: string, signal?: AbortSignal) {
  return api.get(`${base}/convention-passes/registrations/${registrationId}`, {
    schema: standSchema,
    ...withSignal(signal),
  })
}

// --- fit-out ------------------------------------------------------------------------------------

export const FIT_OUT_STATUSES = ['draft', 'submitted', 'approved', 'rejected'] as const

const itemSchema = z.object({
  id: z.string(),
  status: z.enum(FIT_OUT_STATUSES),
  submittedAt: z.string().nullable(),
  reviewedAt: z.string().nullable(),
  reviewNote: z.string().nullable(),
})

export const fitOutSchema = z.object({
  contractor: itemSchema
    .extend({
      contractorName: z.string(),
      contactName: z.string().nullable(),
      contactEmail: z.string().nullable(),
      contactPhone: z.string().nullable(),
      attachments: z.number(),
      // `.default(…)`: the console can be deployed ahead of the API.
      attachmentNames: z.array(z.string()).default([]),
    })
    .nullable(),
  fascia: itemSchema
    .extend({
      fasciaName: z.string(),
      notes: z.string().nullable(),
      hasLogo: z.boolean(),
      logoName: z.string().nullable().default(null),
    })
    .nullable(),
  designPlans: z.array(
    itemSchema.extend({
      title: z.string(),
      notes: z.string().nullable(),
      revision: z.number(),
      hasDrawing: z.boolean(),
      drawingName: z.string().nullable().default(null),
      // `.default(null)`: the console can be deployed ahead of the API.
      maxHeightMeters: z.string().nullable().default(null),
    }),
  ),
})

export type FitOut = z.infer<typeof fitOutSchema>
export type FitOutKind = 'contractor' | 'design_plan' | 'fascia'

export function fetchFitOut(applicationId: string, signal?: AbortSignal): Promise<FitOut> {
  return api.get(`${base}/applications/${applicationId}/fit-out`, {
    schema: fitOutSchema,
    ...withSignal(signal),
  })
}

/**
 * What the organizer enters for the exhibitor. Each one is approved at once. An empty optional
 * field is left out, so the server clears it.
 */
export type FitOutEntry =
  | { kind: 'fascia'; fasciaName: string; notes?: string; logoKey?: string }
  | {
      kind: 'contractor'
      contractorName: string
      contactName?: string
      contactEmail?: string
      contactPhone?: string
      /** Added to the files the appointment has. */
      attachmentKeys?: string[]
    }
  | {
      kind: 'design_plan'
      title: string
      maxHeightMeters?: string
      notes?: string
      drawingKey?: string
    }

const ENTRY_PATH = {
  fascia: { method: 'put', path: 'fascia' },
  contractor: { method: 'put', path: 'contractor' },
  design_plan: { method: 'post', path: 'design-plans' },
} as const

export function saveFitOutEntry(applicationId: string, { kind, ...body }: FitOutEntry) {
  const { method, path } = ENTRY_PATH[kind]
  return api[method](`${base}/applications/${applicationId}/fit-out/${path}`, {
    schema: z.object({ id: z.string() }),
    body,
  })
}

/** Which file of an item: a contractor file by its place in the list, a drawing by its plan. */
export type FitOutFile = { kind: FitOutKind; index?: number; planId?: string }

/** A link that opens one file of the fit-out. It lasts five minutes, so it is fetched on click. */
export function fetchFitOutFileLink(applicationId: string, file: FitOutFile) {
  const query = new URLSearchParams()
  if (file.index !== undefined) query.set('index', String(file.index))
  if (file.planId !== undefined) query.set('planId', file.planId)
  const search = query.size === 0 ? '' : `?${query.toString()}`
  return api.get(`${base}/applications/${applicationId}/fit-out/${file.kind}/file-link${search}`, {
    schema: z.object({ url: z.string(), expiresInSeconds: z.number() }),
  })
}

/** Approve, or reject with a note. The API refuses a rejection with no note. */
export function reviewFitOut(kind: FitOutKind, id: string, approved: boolean, note: string) {
  return api.post(`${base}/fit-out/${kind}/${id}/review`, {
    schema: z.object({ id: z.string() }),
    body: note.trim() === '' ? { approved } : { approved, note: note.trim() },
  })
}
